import React from 'react';
import PropTypes from 'prop-types';
import {
  Flex,
  Popover,
  Typography,
  useI18nContext,
  Link,
} from '@procore/core-react';
import { ExternalLink, Info } from '@procore/core-icons';
import { UnderlineLink } from '@/react/styles/v2';
import { marketplaceColors } from '@/react/styles/constants';

const EMBEDDED_TYPES = ['iframe', 'fullscreen', 'sidepanel'];
const embeddedType = {
  fullscreen: 0,
  sidepanel: 1,
  both: 2,
};

const getEmbeddedType = (components) => {
  if (components.length === 0) {
    return embeddedType.fullscreen;
  }
  let fullscreen = false;
  let sidepanel = false;
  components.forEach((component) => {
    if (component === 'fullscreen' || component === 'iframe') {
      fullscreen = true;
    }
    if (component === 'sidepanel') {
      sidepanel = true;
    }
  });

  if (fullscreen && sidepanel) {
    return embeddedType.both;
  }
  if (fullscreen) {
    return embeddedType.fullscreen;
  }
  return embeddedType.sidepanel;
};

const getType = (components) => {
  if (components.length === 0) {
    return 'dataConnector';
  }
  let dataConnector = false;
  let embedded = false;
  components.forEach((component) => {
    if (component === 'oauth') {
      dataConnector = true;
    }
    if (EMBEDDED_TYPES.includes(component)) {
      embedded = true;
    }
  });

  if (dataConnector && embedded) {
    return 'dataConnectorAndEmbedded';
  }
  if (dataConnector) {
    return 'dataConnector';
  }
  return 'embedded';
};

export const hasEmbedded = (components) => {
  return components.some((component) => EMBEDDED_TYPES.includes(component));
};

export const AppType = ({ components }) => {
  const i18n = useI18nContext();
  const overlay = (
    <Popover.Content>
      <Flex direction="column" gap="sm" padding="lg">
        <Typography className="font-size-xs">
          {i18n.t('appDetails.sideBar.appTypePopover')}
        </Typography>
        <Typography className="font-size-xs">
          <Flex alignItems="center" gap="sm">
            <UnderlineLink>
              <Link
                href="https://developers.procore.com/documentation/building-apps-app-types"
                target="_blank"
              >
                {i18n.t('appDetails.sideBar.appTypeLink')}{' '}
                <ExternalLink size="sm" />
              </Link>
            </UnderlineLink>
          </Flex>
        </Typography>
      </Flex>
    </Popover.Content>
  );
  return (
    <Flex direction="row" gap="sm" alignItems="center" justifyContent="center">
      <Typography
        style={{
          fontSize: '18px',
          lineHeight: '24px',
          color: marketplaceColors.concrete50,
        }}
      >
        {i18n.t(`appDetails.sideBar.appType.${getType(components)}`)}
      </Typography>
      <Popover overlay={overlay} trigger="hover" key="example-2">
        {() => <Info size="sm" />}
      </Popover>
    </Flex>
  );
};

AppType.propTypes = {
  components: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const EmbeddedTypePopover = ({ type }) => {
  const i18n = useI18nContext();
  let content = '';
  switch (type) {
    case embeddedType.fullscreen:
      content = i18n.t('appDetails.sideBar.embeddedTypePopover.fullscreen');
      break;
    case embeddedType.sidepanel:
      content = i18n.t('appDetails.sideBar.embeddedTypePopover.sidepanel');
      break;
    case embeddedType.both:
      content = i18n.t('appDetails.sideBar.embeddedTypePopover.both');
      break;
    default:
      content = 'missingContent';
  }
  return <Typography className="font-size-xs">{content}</Typography>;
};

EmbeddedTypePopover.propTypes = {
  type: PropTypes.number.isRequired,
};

export const EmbeddedType = ({ components }) => {
  const i18n = useI18nContext();
  const translatedComponents = components
    .filter((component) => EMBEDDED_TYPES.includes(component))
    .map((component) => i18n.t(`appDetails.sideBar.displayType.${component}`));
  const embeddedType = getEmbeddedType(components);
  const overlay = (
    <Popover.Content>
      <Flex direction="column" gap="sm" padding="lg">
        <EmbeddedTypePopover type={embeddedType} />
        <Typography className="font-size-xs">
          <Flex alignItems="center" gap="sm">
            <UnderlineLink>
              <Link
                href="https://developers.procore.com/documentation/building-apps-app-types"
                target="_blank"
              >
                {i18n.t('appDetails.sideBar.embeddedType')}{' '}
                <ExternalLink size="sm" />
              </Link>
            </UnderlineLink>
          </Flex>
        </Typography>
      </Flex>
    </Popover.Content>
  );
  return (
    <Flex direction="row" gap="sm" alignItems="center" justifyContent="center">
      <Typography className="font-size-md color-concrete">
        {translatedComponents.join(', ')}
      </Typography>
      <Popover overlay={overlay} trigger="hover" key="example-2">
        {() => <Info size="sm" />}
      </Popover>
    </Flex>
  );
};

EmbeddedType.propTypes = {
  components: PropTypes.arrayOf(PropTypes.string).isRequired,
};
